/**
 * Formulaire de soumission d'appel contre un ban
 * 
 * Permet à un utilisateur banni de soumettre un recours
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { fr, enUS, arSA } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const STEPS = [
  'Comprendre le processus',
  'Rédiger votre appel',
  'Soumettre'
];

const BanAppealForm = ({ restrictionId, onSuccess, onCancel }) => {
  const { t, i18n } = useTranslation('banAppealForm');
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Données de vérification
  const [canAppeal, setCanAppeal] = useState(null);
  
  // Données du formulaire
  const [reason, setReason] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [attachments, setAttachments] = useState([]);
  
  // Expansion des sections
  const [showGuidelines, setShowGuidelines] = useState(true);
  
  // Formater les dates en DD/MM/YYYY avec support multilingue
  const formatDate = (date) => {
    if (!date) return null;
    const localeMap = { fr, en: enUS, ar: arSA };
    const locale = localeMap[i18n.language] || fr;
    return format(new Date(date), "dd/MM/yyyy", { locale });
  };
  
  // Vérifier si l'utilisateur peut faire appel
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/appeals/can-submit/${restrictionId}`
        );
        setCanAppeal(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || t('error.verification_failed'));
      } finally {
        setLoading(false);
      }
    };
    
    if (restrictionId) {
      checkEligibility();
    }
  }, [restrictionId]);
  
  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  
  const handleSubmit = async () => {
    if (reason.length < 50) {
      setError(t('error.minimum_characters'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('restrictionId', restrictionId);
      formData.append('reason', reason);
      if (additionalInfo) formData.append('additionalInfo', additionalInfo);

      // Pièces jointes (0..10)
      (attachments || []).forEach(file => {
        formData.append('attachments', file);
      });

      await api.post('/appeals', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      if (onSuccess) onSuccess();

    } catch (err) {
      setError(err.response?.data?.message || t('error.submission_failed'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Affichage du chargement
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Impossible de faire appel
  if (!canAppeal?.canAppeal) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t('alerts.appeal_not_possible')}</AlertTitle>
          {canAppeal?.reason === 'DEADLINE_PASSED' && (
            <>{t('alerts.deadline_passed')}</>
          )}
          {canAppeal?.reason === 'APPEAL_PENDING' && (
            <>{t('alerts.appeal_pending')}</>
          )}
          {canAppeal?.reason === 'COOLDOWN_ACTIVE' && (
            <>
              {t('alerts.cooldown_active')}
              {canAppeal.cooldownEnd && (
                <Box mt={1}>
                  <strong>{t('labels.possible_date')}:</strong>{' '}
                  {formatDate(canAppeal.cooldownEnd)}
                </Box>
              )}
            </>
          )}
          {canAppeal?.reason === 'NO_ACTIVE_BAN' && (
            <>{t('alerts.no_active_ban')}</>
          )}
        </Alert>
        
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            {t('buttons.back')}
          </Button>
        )}
      </Paper>
    );
  }
  
  // Succès
  if (success) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('success.title')}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {t('success.message')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('success.notification')}
        </Typography>
      </Paper>
    );
  }
  
  // Formulaire principal
  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <GavelIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5">
            {t('title.main')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canAppeal.deadline && (
              <>
                {t('labels.remaining_days')}: {differenceInDays(new Date(canAppeal.deadline), new Date())} {t('labels.days')}
              </>
            )}
          </Typography>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Étape 1: Comprendre le processus */}
        <Step>
          <StepLabel>
            <Typography variant="subtitle1">{t('steps.understand')}</Typography>
          </StepLabel>
          <StepContent>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center"
                  onClick={() => setShowGuidelines(!showGuidelines)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t('guidelines.title')}
                  </Typography>
                  <IconButton size="small">
                    {showGuidelines ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={showGuidelines}>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText 
                        primary={t('guidelines.honest')}
                        secondary={t('guidelines.honest_desc')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText 
                        primary={t('guidelines.acknowledge')}
                        secondary={t('guidelines.acknowledge_desc')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                      <ListItemText 
                        primary={t('guidelines.explain')}
                        secondary={t('guidelines.explain_desc')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                      <ListItemText 
                        primary={t('guidelines.avoid')}
                        secondary={t('guidelines.avoid_desc')}
                      />
                    </ListItem>
                  </List>
                </Collapse>
              </CardContent>
            </Card>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t('process.review_title')}</AlertTitle>
              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <TimeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('process.review_time')} />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <DescriptionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('process.complete_review')} />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <GavelIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('process.final_decision')} />
                </ListItem>
              </List>
            </Alert>
            
            <Box display="flex" gap={1}>
              <Button variant="contained" onClick={handleNext}>
                {t('buttons.understand_continue')}
              </Button>
              {onCancel && (
                <Button variant="outlined" onClick={onCancel}>
                  {t('buttons.cancel')}
                </Button>
              )}
            </Box>
          </StepContent>
        </Step>
        
        {/* Étape 2: Rédiger l'appel */}
        <Step>
          <StepLabel>
            <Typography variant="subtitle1">{t('steps.write_appeal')}</Typography>
          </StepLabel>
          <StepContent>
            <TextField
              fullWidth
              multiline
              rows={6}
              label={t('form.reason_label')}
              placeholder={t('form.reason_placeholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText={t('form.character_count', { current: reason.length, max: 2000 })}
              error={reason.length > 0 && reason.length < 50}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 2000 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('form.additional_info_label')}
              placeholder={t('form.additional_info_placeholder')}
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              helperText={t('form.additional_character_count', { current: additionalInfo.length, max: 5000 })}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 5000 }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('form.attachments_label')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('form.attachments_description')}
              </Typography>
              <Button variant="outlined" component="label">
                {t('buttons.choose_files')}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setAttachments(files.slice(0, 10));
                  }}
                />
              </Button>

              {(attachments || []).length > 0 && (
                <List dense sx={{ mt: 1 }}>
                  {attachments.map((f, idx) => (
                    <ListItem
                      key={`${f.name}-${idx}`}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label={t('buttons.remove')}
                          onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <DescriptionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={f.name}
                        secondary={`${Math.round((f.size || 0) / 1024)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            
            <Box display="flex" gap={1}>
              <Button variant="outlined" onClick={handleBack}>
                {t('buttons.back')}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={reason.length < 50}
              >
                {t('buttons.continue')}
              </Button>
            </Box>
          </StepContent>
        </Step>
        
        {/* Étape 3: Soumettre */}
        <Step>
          <StepLabel>
            <Typography variant="subtitle1">{t('steps.submit')}</Typography>
          </StepLabel>
          <StepContent>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>{t('submit.alert_title')}</AlertTitle>
              {t('submit.alert_message')}
            </Alert>
            
            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  {t('submit.explanation_label')}:
                </Typography>
                <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                  {reason}
                </Typography>
                
                {additionalInfo && (
                  <>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      {t('submit.additional_info_label')}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {additionalInfo}
                    </Typography>
                  </>
                )}

                {(attachments || []).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                      {t('submit.attachments_label')}:
                    </Typography>
                    <List dense>
                      {attachments.map((f, idx) => (
                        <ListItem key={`${f.name}-${idx}`}>
                          <ListItemIcon>
                            <DescriptionIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={f.name}
                            secondary={`${Math.round((f.size || 0) / 1024)} KB`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
            
            <Box display="flex" gap={1}>
              <Button variant="outlined" onClick={handleBack}>
                {t('buttons.edit')}
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {submitting ? t('submit.submitting') : t('submit.button')}
              </Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </Paper>
  );
};

export default BanAppealForm;
